import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { logoutUser } from '@/utils/auth'
import { recordRumEvent } from '@/configs/observability'

// Transient gateway errors: load balancer has no healthy backend (503
// "no available server") or upstream timed out. Retry idempotent GETs a few
// times with backoff so a brief backend blip self-heals instead of surfacing
// an error to the user and spamming Sentry.
const MAX_RETRIES = 2
const RETRY_BASE_DELAY_MS = 600
const TRANSIENT_STATUSES = [502, 503, 504]

const isRetriableTransient = (error) => {
  const method = (error?.config?.method || '').toLowerCase()
  if (method !== 'get' && method !== 'head') return false
  if (!error.response) return Boolean(error.request) // network error / no response
  return TRANSIENT_STATUSES.includes(error.response.status)
}

const retryDelay = (attempt) => RETRY_BASE_DELAY_MS * Math.pow(2, attempt) + Math.floor(Math.random() * 200)

export default function setup() {
  // No request should hang forever: a backend call that never responds
  // must not leave the UI (e.g. the login spinner) stuck indefinitely.
  axios.defaults.timeout = 15000

  const observeResponse = (config, outcome, status) => {
    if (!config || config.__observabilityCompleted) return
    config.__observabilityCompleted = true
    if (config.__observabilityStartedAt) recordRumEvent('api_request', {
      method: (config.method || 'GET').toUpperCase(), outcome, status,
      duration_ms: Math.max(0, Date.now() - config.__observabilityStartedAt)
    })
  }
  axios.interceptors.request.use((config) => {
    if (!config.__observabilityStartedAt) config.__observabilityStartedAt = Date.now()
    return config
  })
  // Add a response interceptor
  axios.interceptors.response.use((response) => {
    observeResponse(response.config, 'succeeded', response.status)
    return response
  }, (error) => {
    const responseURL = error?.request?.responseURL || ''
    const urlLogin = responseURL ? responseURL.split("/") : []
    const lastURLSegment = urlLogin[urlLogin.length - 1]

    //catches if the session ended!
    if (error.response && error.response.status === 401 && lastURLSegment != 'auth?lang=id') {
      setTimeout(() => {
        logoutUser(true)
      }, 1000)
    }

    // 403 Forbidden - user authenticated but lacks permission, do not logout
    if (error.response && error.response.status === 403) {
      observeResponse(error.config, 'failed', 403)
      return Promise.reject(error)
    }

    // retry transient gateway errors on idempotent requests before giving up
    if (error.config && isRetriableTransient(error)) {
      const attempt = error.config.__retryCount || 0
      if (attempt < MAX_RETRIES) {
        error.config.__retryCount = attempt + 1
        recordRumEvent('api_retry', { method: error.config.method.toUpperCase(), attempt: attempt + 1, status: error.response ? error.response.status : 0 })
        return new Promise((resolve) => setTimeout(resolve, retryDelay(attempt)))
          .then(() => axios(error.config))
      }
    }

    observeResponse(error.config, 'failed', error.response ? error.response.status : 0)
    // capture unexpected HTTP errors to Sentry
    // skip: 401 (session), 403 (permission), 404 (not found), 409 (precondition conflict — handled by caller)
    if (error.response) {
      const status = error.response.status
      if (status !== 401 && status !== 403 && status !== 404 && status !== 409) {
        Sentry.captureException(error, {
          extra: {
            url: error.config && error.config.url && error.config.url.split('?')[0],
            method: error.config && error.config.method,
            status
          }
        })
      }
    } else if (error.request) {
      Sentry.captureException(error, {
        extra: {
          url: error.config && error.config.url && error.config.url.split('?')[0]
        }
      })
    }

    return Promise.reject(error)
  })
}
